import { ACEPTED_STATUS_ID, ADD_SUP_IVA_STATUS, ADMITTED_STATUS, CHANGE_TO_REDUCED, ORDER_STATUS_ID_REQUESTED, PROCESS_IVA_STATUS, SUPPLEMENT_STATUS } from 'utility/constants'
import { isSpecialRequestedOrder } from './specialStores'

type Supplement = { 'status_id': number, volumetric: boolean, deleted: boolean }
type Eligibility = {
  'status_id': number
  'estimated_costs_status': number | null
  'iva_status': string | null
  supplements: Supplement[]
}

// This is display eligibility, never permission to execute a purchase.
export function isPurchasableSpecialOrder (order: Eligibility): boolean {
  if (![ORDER_STATUS_ID_REQUESTED, SUPPLEMENT_STATUS].includes(order.status_id)) return false
  if (!Array.isArray(order.supplements)) return false
  const supplements = order.supplements.filter(s => s.deleted !== true)
  // Missing/unknown payment or type information must not imply payment.
  if (supplements.some(s => typeof s.volumetric !== 'boolean')) return false
  const nonVolumetric = supplements.filter(s => !s.volumetric)
  if (nonVolumetric.some(s => s.status_id !== ADMITTED_STATUS)) return false
  if (order.estimated_costs_status !== null && order.estimated_costs_status !== ACEPTED_STATUS_ID) return false
  const paidSupplement = nonVolumetric.length > 0
  const acceptedEstimate = order.status_id === ORDER_STATUS_ID_REQUESTED && order.estimated_costs_status === ACEPTED_STATUS_ID
  // Paid/accepted customer decisions take priority over IVA and volumetric notices.
  if (paidSupplement || acceptedEstimate) return true
  if (order.status_id !== ORDER_STATUS_ID_REQUESTED || supplements.length > 0) return false
  return order.iva_status === null || [PROCESS_IVA_STATUS, ADD_SUP_IVA_STATUS, CHANGE_TO_REDUCED].includes(order.iva_status)
}

export async function getPurchasableOrderGroups (client: any): Promise<{ ids: number[], specialIds: number[] }> {
  const ids: number[] = []
  const specialIds = new Set<number>()
  let after = -1
  while (true) {
    const { data, error } = await client.from('orders')
      .select('id, status_id, estimated_costs_status, iva_status, supplements(status_id, volumetric, deleted), custom_marketplace_id, marketplace_id(id, original_url), items(url, custom_order_site, deleted)')
      .in('status_id', [ORDER_STATUS_ID_REQUESTED, SUPPLEMENT_STATUS])
      .gt('id', after).order('id', { ascending: true }).limit(500)
    if (error || !Array.isArray(data)) throw new Error('No se pudo comprobar la elegibilidad de los pedidos ejecutables')
    if (!data.length) break
    for (const order of data) {
      if (isPurchasableSpecialOrder(order)) {
        ids.push(order.id)
        if (isSpecialRequestedOrder(order)) specialIds.add(order.id)
      }
    }
    const lastId = data[data.length - 1].id
    if (lastId <= after) throw new Error('Special order classification did not advance')
    after = lastId
  }
  const purchased = new Set<number>()
  for (let offset = 0; offset < ids.length; offset += 200) {
    for (let start = 0; ; start += 1000) {
      const { data, error } = await client.from('market_orders').select('order_id')
        .in('order_id', ids.slice(offset, offset + 200)).eq('deleted', false).range(start, start + 999)
      if (error || !Array.isArray(data)) throw new Error('No se pudo comprobar si los pedidos ejecutables ya están comprados')
      data.forEach((row: { 'order_id': number }) => purchased.add(row.order_id))
      if (data.length < 1000) break
    }
  }
  const eligibleIds = ids.filter(id => !purchased.has(id))
  return { ids: eligibleIds, specialIds: eligibleIds.filter(id => specialIds.has(id)) }
}

export async function getPurchasableSpecialOrderIds (client: any): Promise<number[]> {
  return (await getPurchasableOrderGroups(client)).specialIds
}
